import { PaletteMode, PaletteOptions } from '@mui/material';
import { GetThemeOptions } from '../theme';
import {
  unitySemanticDarkColorTokens,
  unitySemanticLightColorTokens,
  xcorDefinedDarkTokens,
  xcorDefinedLightTokens,
} from '../unityTokens';
import { green, grey, red, white } from './colors';
import {
  buildPaletteFromMetadata,
  nonColorPaletteToken,
  PaletteTokenMetadata,
  Token,
} from './panw-palette/token-metadata';

/** For use strictly within @chronosphereio/chrono-ui and @chronosphereio/viz-framework */
const buttonTokens = [
  { propertyPath: 'standard.primary.borderColor', tokenName: nonColorPaletteToken('empty-object') },
  { propertyPath: 'standard.primary.default', tokenName: '--semantic-bg-interactive-highlight-default' },
  { propertyPath: 'standard.primary.disabled', tokenName: '--semantic-bg-interactive-neutral-disabled' },
  { propertyPath: 'standard.primary.hover', tokenName: '--semantic-bg-interactive-highlight-hover' },
  { propertyPath: 'standard.primary.text', tokenName: '--semantic-text-inverse' },
  { propertyPath: 'standard.primary.textDisabled', tokenName: '--semantic-text-disabled' },
  { propertyPath: 'standard.secondary.borderColor', tokenName: nonColorPaletteToken('empty-object') },
  { propertyPath: 'standard.secondary.default', tokenName: '--semantic-bg-interactive-neutral-default' },
  { propertyPath: 'standard.secondary.disabled', tokenName: '--semantic-bg-interactive-neutral-disabled' },
  { propertyPath: 'standard.secondary.hover', tokenName: '--semantic-bg-interactive-neutral-hover' },
  { propertyPath: 'standard.secondary.text', tokenName: '--semantic-text-bold' },
  { propertyPath: 'standard.secondary.textDisabled', tokenName: '--semantic-text-disabled' },
  { propertyPath: 'standard.tertiary.disabled', tokenName: nonColorPaletteToken('unset') },
  { propertyPath: 'standard.tertiary.hover', tokenName: '--bg-hover' },
  { propertyPath: 'standard.tertiary.text', tokenName: '--semantic-text-bold' },
  { propertyPath: 'danger.primary.borderColor', tokenName: nonColorPaletteToken('empty-object') },
  { propertyPath: 'danger.primary.default', tokenName: '--semantic-bg-interactive-destructive-default' },
  { propertyPath: 'danger.primary.disabled', tokenName: '--semantic-bg-interactive-destructive-disabled' },
  { propertyPath: 'danger.primary.hover', tokenName: '--semantic-bg-interactive-destructive-hover' },
  { propertyPath: 'danger.primary.text', tokenName: '--semantic-text-contrast' },
  { propertyPath: 'danger.primary.textDisabled', tokenName: '--semantic-text-disabled' },
  { propertyPath: 'danger.tertiary.disabled', tokenName: nonColorPaletteToken('unset') },
  { propertyPath: 'danger.tertiary.hover', tokenName: '--semantic-bg-interactive-destructive-subtle-default' },
  { propertyPath: 'danger.tertiary.text', tokenName: '--semantic-text-destructive' },
  { propertyPath: 'danger.tertiary.textDisabled', tokenName: '--semantic-text-disabled' },
] satisfies Token[];

export const buttonTokenMetadata: PaletteTokenMetadata = {
  componentName: 'button',
  tokens: buttonTokens,
};

export const button = (mode: PaletteMode, opts: GetThemeOptions): PaletteOptions['button'] => {
  if (opts.panwRebrandStubYear) {
    const tokens = mode === 'light' ? unitySemanticLightColorTokens : unitySemanticDarkColorTokens;
    const xcorTokens = mode === 'light' ? xcorDefinedLightTokens : xcorDefinedDarkTokens;
    return buildPaletteFromMetadata(buttonTokenMetadata.tokens, tokens, xcorTokens);
  }

  return mode === 'light'
    ? {
        standard: {
          primary: {
            borderColor: {},
            default: green[600],
            disabled: grey[100],
            hover: green[800],
            text: grey[50],
            textDisabled: grey[300],
          },
          secondary: {
            borderColor: {
              default: grey[300],
              disabled: grey[300],
              hover: grey[600],
            },
            default: white,
            hover: grey[50],
            text: grey[950],
            disabled: 'transparent',
            textDisabled: grey[300],
          },
          tertiary: {
            hover: grey[100],
            text: grey[500],
          },
        },
        danger: {
          primary: {
            borderColor: {},
            default: red[600],
            disabled: grey[100],
            hover: red[700],
            text: grey[50],
            textDisabled: grey[300],
          },
          tertiary: {
            hover: red[100],
            text: red[600],
            textDisabled: grey[300],
          },
        },
      }
    : {
        standard: {
          primary: {
            borderColor: {},
            default: green[500],
            disabled: grey[850],
            hover: green[300],
            text: grey[950],
            textDisabled: grey[500],
          },
          secondary: {
            borderColor: {
              default: grey[600],
              disabled: grey[600],
              hover: grey[200],
            },
            default: grey[900],
            hover: grey[800],
            text: grey[50],
            disabled: 'transparent',
            textDisabled: grey[500],
          },
          tertiary: {
            hover: 'transparent',
            text: grey[200],
          },
        },
        danger: {
          primary: {
            borderColor: {},
            default: red[500],
            disabled: grey[850],
            hover: red[100],
            text: grey[950],
            textDisabled: grey[500],
          },
          tertiary: {
            hover: red[800],
            text: red[500],
            textDisabled: grey[500],
          },
        },
      };
};
